/**
 * Period-Filter-Hook: kombiniert SegmentedControl-Mode (year/quarter/month/custom)
 * mit Jahr-Slider, baut den Backend-Period-String und persistiert in URL-Query.
 *
 * URL-Format: ?period=2026 | 2026-Q1 | 2026-03 | 2026-01-01..2026-06-30
 */
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

export type PeriodMode = 'year' | 'quarter' | 'month' | 'custom';

export interface PeriodState {
  mode: PeriodMode;
  year: number;
  quarter: number; // 1..4 nur relevant wenn mode='quarter'
  month: number; // 1..12 nur relevant wenn mode='month'
  customFrom: string; // ISO YYYY-MM-DD nur wenn mode='custom'
  customTo: string;
}

const CURRENT_YEAR = new Date().getUTCFullYear();
const CURRENT_MONTH = new Date().getUTCMonth() + 1;
const CURRENT_QUARTER = Math.ceil(CURRENT_MONTH / 3);

const DEFAULT: PeriodState = {
  mode: 'year',
  year: CURRENT_YEAR,
  quarter: CURRENT_QUARTER,
  month: CURRENT_MONTH,
  customFrom: `${CURRENT_YEAR}-01-01`,
  customTo: `${CURRENT_YEAR}-12-31`,
};

/** Decodet die period-URL in den State. Falsche Werte → Default. */
export function decodePeriod(period: string | undefined): PeriodState {
  if (!period) return DEFAULT;
  if (period.includes('..')) {
    const [from, to] = period.split('..');
    if (from && to) {
      return { ...DEFAULT, mode: 'custom', customFrom: from, customTo: to };
    }
  }
  if (/^\d{4}-Q[1-4]$/i.test(period)) {
    const [y, q] = period.split('-Q');
    return { ...DEFAULT, mode: 'quarter', year: Number(y), quarter: Number(q) };
  }
  if (/^\d{4}-\d{2}$/.test(period)) {
    const [y, m] = period.split('-');
    return { ...DEFAULT, mode: 'month', year: Number(y), month: Number(m) };
  }
  if (/^\d{4}$/.test(period)) {
    return { ...DEFAULT, mode: 'year', year: Number(period) };
  }
  return DEFAULT;
}

export function encodePeriod(s: PeriodState): string {
  switch (s.mode) {
    case 'quarter':
      return `${s.year}-Q${s.quarter}`;
    case 'month':
      return `${s.year}-${String(s.month).padStart(2, '0')}`;
    case 'custom':
      return `${s.customFrom}..${s.customTo}`;
    default:
      return String(s.year);
  }
}

export function usePeriodFilter() {
  const search = useSearch({ strict: false }) as { period?: string };
  const navigate = useNavigate();
  const state = decodePeriod(search.period);

  const setState = useCallback(
    (next: Partial<PeriodState>) => {
      const merged = { ...state, ...next };
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          period: encodePeriod(merged),
        })) as unknown as true,
        replace: true,
      });
    },
    [state, navigate],
  );

  return { state, setState, periodString: encodePeriod(state) };
}
